/**
 * The calls the AI took part in: what it heard, what it said, and how long the
 * caller waited for it.
 *
 * The AI part of a call is recorded as audio now (`lib/agent/tape.ts`), but
 * this list is still where the owner reads each turn beside its timing - and
 * the place they find out whether the settings and the knowledge text above
 * are right. Calls the AI placed itself are listed too, named by the number it
 * rang rather than by our own line, and marked when that was a test number.
 * The headline is the one number the prototype is judged on: from the moment
 * the caller stops talking to the moment the AI's first sound leaves our
 * server, target 1.5-2.1 s (docs/crm-calls-ai-uuring.md). Seconds with one
 * decimal, because that is the resolution the difference between "fine" and
 * "awkward" lives at.
 */
import { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight, History, Loader2, RefreshCw } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../../../i18n/I18nProvider";
import { callCounterpart } from "../../../../../lib/calls";
import { formatPhone } from "../../../../../lib/phone";
import {
  AGENT_OUTCOMES,
  AGENT_UNFINISHED_OUTCOME,
  AI_AGENT_ENTRIES,
  listRecentAgentCalls,
  type AgentCallRow,
  type AgentCallsSummary,
  type AgentRecentCalls,
  type AgentTurn,
} from "../../../../../lib/telephonyAgent";
import { Btn, StatusPill, fmtDateTime, fmtNum, type StatusTone } from "../../../../ui";
import { IntegrationCard } from "../../panel/IntegrationCard";
import { Note } from "../../panel/fields";

const LIMIT = 20;

/** A small caps label in front of a line of the conversation. */
const LINE_LABEL = "crm-sub mr-1.5 text-[11px] font-[650] uppercase tracking-[0.05em]";

/** Settled endings are green, the caller leaving is neutral, running out of time amber, a fault red. */
const OUTCOME_TONE: Record<string, StatusTone> = {
  handoff: "ok",
  voicemail: "ok",
  goodbye: "ok",
  hangup: "mut",
  timeout: "wt",
  error: "lt",
};

type Translate = ReturnType<typeof useT>;

/** Milliseconds as seconds with one decimal, in the Estonian format every figure in the CRM uses. */
function secs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || !Number.isFinite(ms)) return "-";
  return `${(ms / 1000).toLocaleString("et-EE", { minimumFractionDigits: 1, maximumFractionDigits: 1 })} s`;
}

/**
 * No ending yet reads the same whether the row says so with null or the
 * summary with its own key. A value from a newer server is shown as it is,
 * rather than as a dotted key.
 */
function outcomeLabel(t: Translate, outcome: string | null): string {
  if (!outcome || outcome === AGENT_UNFINISHED_OUTCOME) return t("settings.aiAgent.recent.outcome.none");
  return (AGENT_OUTCOMES as readonly string[]).includes(outcome)
    ? t(`settings.aiAgent.recent.outcome.${outcome}`)
    : outcome;
}

function entryLabel(t: Translate, entry: string | null): string {
  if (!entry) return "";
  return (AI_AGENT_ENTRIES as readonly string[]).includes(entry) ? t(`settings.aiAgent.recent.entry.${entry}`) : entry;
}

export function RecentAgentCalls() {
  const t = useT();
  const [data, setData] = useState<AgentRecentCalls | null>(null);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());

  const load = useCallback(() => {
    setLoading(true);
    listRecentAgentCalls(LIMIT)
      .then((next) => {
        setData(next);
        setFailed(false);
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function toggle(id: string) {
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const calls = data?.calls ?? [];

  return (
    <IntegrationCard
      icon={History}
      title={t("settings.aiAgent.recent.title")}
      desc={t("settings.aiAgent.recent.desc")}
      status={
        <Btn small onClick={load} disabled={loading}>
          <RefreshCw className={cn("size-3.5", loading && "animate-spin")} />
          {t("settings.aiAgent.recent.refresh")}
        </Btn>
      }
    >
      <div className="space-y-4">
        {failed ? <Note tone="bad">{t("settings.aiAgent.recent.loadError")}</Note> : null}
        {loading && !data ? (
          <div className="flex justify-center py-6 text-muted-foreground">
            <Loader2 className="size-5 animate-spin" />
          </div>
        ) : null}
        {data ? <Summary summary={data.summary} /> : null}
        {data && calls.length === 0 ? <Note tone="mut">{t("settings.aiAgent.recent.empty")}</Note> : null}
        {calls.length > 0 ? (
          <ul className="space-y-2">
            {calls.map((call) => (
              <CallRow key={call.id} call={call} open={open.has(call.id)} onToggle={() => toggle(call.id)} />
            ))}
          </ul>
        ) : null}
      </div>
    </IntegrationCard>
  );
}

function Summary({ summary }: { summary: AgentCallsSummary }) {
  const t = useT();
  const fa = summary.firstAudio;
  const tiles = [
    { key: "calls", label: t("settings.aiAgent.recent.kpiCalls"), value: fmtNum(summary.calls) },
    { key: "turns", label: t("settings.aiAgent.recent.kpiTurns"), value: fmtNum(summary.turns) },
    { key: "mean", label: t("settings.aiAgent.recent.kpiMean"), value: secs(fa?.mean) },
    { key: "median", label: t("settings.aiAgent.recent.kpiMedian"), value: secs(fa?.median) },
    { key: "p90", label: t("settings.aiAgent.recent.kpiP90"), value: secs(fa?.p90) },
    { key: "bargeIns", label: t("settings.aiAgent.recent.kpiBargeIns"), value: fmtNum(summary.bargeIns) },
  ];
  const outcomes = Object.entries(summary.outcomes ?? {}).filter(
    (entry): entry is [string, number] => typeof entry[1] === "number" && entry[1] > 0,
  );

  return (
    <div className="space-y-2.5">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-7">
        {tiles.map((tile) => (
          <div key={tile.key} className="min-w-0 rounded-2xl bg-[var(--crm-sunk)] px-3.5 py-2.5">
            <span className="crm-sub block truncate text-[11px]">{tile.label}</span>
            <span className="crm-tnum block text-[15px] font-[650]">{tile.value}</span>
          </div>
        ))}
      </div>
      <p className="crm-sub text-[11.5px] leading-snug">
        {t("settings.aiAgent.recent.latencyAbout", { count: fmtNum(fa?.n ?? 0) })}
      </p>
      {outcomes.length > 0 ? (
        <div className="flex flex-wrap gap-1.5">
          {outcomes.map(([outcome, count]) => (
            <StatusPill key={outcome} tone={OUTCOME_TONE[outcome] ?? "info"}>
              {outcomeLabel(t, outcome)} {fmtNum(count)}
            </StatusPill>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function CallRow({ call, open, onToggle }: { call: AgentCallRow; open: boolean; onToggle: () => void }) {
  const t = useT();
  const turns = call.turns ?? [];
  // The other person, whichever column holds them: on a call the AI placed,
  // `fromNumber` is our own line and `toNumber` the one that rang.
  const other = callCounterpart(call);
  const number = other ? formatPhone(other) : "";
  const who = call.companyName?.trim() || number || t("settings.aiAgent.recent.unknownCaller");
  const tone = (call.agentOutcome && OUTCOME_TONE[call.agentOutcome]) || "info";

  return (
    <li className="overflow-hidden rounded-[20px] bg-[var(--crm-sunk)]">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex w-full min-w-0 flex-wrap items-center gap-x-3 gap-y-1 px-4 py-3 text-left"
      >
        {open ? <ChevronDown className="size-4 shrink-0" /> : <ChevronRight className="size-4 shrink-0" />}
        <span className="crm-tnum shrink-0 text-[12.5px]">{fmtDateTime(call.agentStartedAt ?? call.startedAt)}</span>
        <span className="min-w-0 flex-1 truncate text-[13px] font-[600]">
          {who}
          {call.companyName && number ? (
            <span className="crm-sub ml-2 font-mono text-[11.5px] font-normal">{number}</span>
          ) : null}
        </span>
        <span className="crm-sub text-[12px]">{entryLabel(t, call.agentEntry)}</span>
        {call.agentTest ? <StatusPill tone="wt">{t("settings.aiAgent.recent.test")}</StatusPill> : null}
        <span className="crm-sub text-[12px]">{t("settings.aiAgent.recent.turnCount", { n: turns.length })}</span>
        <StatusPill tone={tone}>{outcomeLabel(t, call.agentOutcome)}</StatusPill>
      </button>
      {open ? <TurnList call={call} turns={turns} /> : null}
    </li>
  );
}

function TurnList({ call, turns }: { call: AgentCallRow; turns: AgentTurn[] }) {
  const t = useT();
  return (
    <div className="space-y-2.5 border-t border-[var(--crm-hair)] px-4 pb-3.5 pt-3">
      {turns.length === 0 ? (
        <p className="crm-sub text-[12.5px]">{t("settings.aiAgent.recent.noTurns")}</p>
      ) : (
        <ol className="space-y-2">
          {/* Turns carry no id of their own; the list is fixed once written. */}
          {turns.map((turn, i) => (
            <TurnItem key={i} turn={turn} />
          ))}
        </ol>
      )}
      <p className="crm-sub text-[11.5px]">
        {t("settings.aiAgent.recent.usage", {
          tokensIn: fmtNum(call.tokensIn ?? 0),
          tokensOut: fmtNum(call.tokensOut ?? 0),
          ttsChars: fmtNum(call.ttsChars ?? 0),
        })}
      </p>
    </div>
  );
}

function TurnItem({ turn }: { turn: AgentTurn }) {
  const t = useT();
  const control = turn.control === "handoff" || turn.control === "end" ? turn.control : null;
  const flags = [
    turn.bargedIn ? t("settings.aiAgent.recent.bargeIn") : null,
    turn.cutShort ? t("settings.aiAgent.recent.cutShort") : null,
    control ? t(`settings.aiAgent.recent.control.${control}`) : null,
  ].filter((flag): flag is string => flag !== null);

  return (
    <li className="rounded-2xl bg-[var(--crm-surface)] px-3.5 py-2.5 text-[12.75px] leading-relaxed">
      <p className="break-words">
        <span className={LINE_LABEL}>{t("settings.aiAgent.recent.heard")}</span>
        {turn.heard || <span className="crm-sub">{t("settings.aiAgent.recent.nothingHeard")}</span>}
      </p>
      <p className="mt-1 break-words">
        <span className={LINE_LABEL}>{t("settings.aiAgent.recent.said")}</span>
        {turn.said || <span className="crm-sub">{t("settings.aiAgent.recent.nothingSaid")}</span>}
      </p>
      <div className="crm-sub mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px]">
        <span className="crm-tnum font-[650]">
          {t("settings.aiAgent.recent.firstAudio", { s: secs(turn.firstAudioMs) })}
        </span>
        <span className="crm-tnum">{t("settings.aiAgent.recent.stt", { s: secs(turn.sttMs) })}</span>
        <span className="crm-tnum">{t("settings.aiAgent.recent.llm", { s: secs(turn.llmFirstSentenceMs) })}</span>
        {flags.map((flag) => (
          <StatusPill key={flag} tone="wt">
            {flag}
          </StatusPill>
        ))}
      </div>
    </li>
  );
}
